import React, { useMemo } from 'react';
import { useGlobalVariables } from '../../contexts/GlobalVariablesContext';
import './ProgressBarRenderer.css';

const ProgressBarRenderer = ({ chartData, error, isEditMode }) => {
  // 디버그 로그 제거
  
  // 데이터 구조 확인 - DynamicChart와 동일한 구조 사용
  // chartData가 없을 때를 대비한 안전한 기본값 설정
  const actualData = chartData?.chart_options?.data || chartData?.data || {};
  const actualOptions = chartData?.chart_options?.options || chartData?.options || {};
  
  // 디버그 로그 제거
  
  // 데이터 추출 - Hook은 조건문 전에 호출
  const value = useMemo(() => {
    // series가 있는 경우
    if (actualData?.series?.[0]?.values?.[0] !== undefined) {
      const val = actualData.series[0].values[0];
      return val;
    }
    // 대체: 직접 value 속성이 있는 경우
    if (actualData?.value !== undefined) {
      return actualData.value;
    }
    return 0;
  }, [actualData]);
  
  const name = useMemo(() => {
    return actualData?.series?.[0]?.name || actualData?.name || '';
  }, [actualData]);
  
  // 옵션 추출
  const min = actualOptions?.min || 0;
  const max = actualOptions?.max || 100;
  const unit = actualOptions?.unit || '%';
  const title = actualOptions?.title || chartData.chart_options?.title || '';
  
  // 퍼센트 계산
  const percentage = ((value - min) / (max - min)) * 100;
  
  // 비교값 설정 추출
  const showComparison = actualOptions?.advanced?.showComparison || false;
  const comparisonValue = actualOptions?.advanced?.comparisonValue;
  const comparisonLabel = actualOptions?.advanced?.comparisonLabel || '';
  const comparisonColor = actualOptions?.advanced?.comparisonColor || '#ff4d4f';
  const comparisonLineWidth = actualOptions?.advanced?.comparisonLineWidth || 2;
  const comparisonLineStyle = actualOptions?.advanced?.comparisonLineStyle || 'solid';
  const { getVariable } = useGlobalVariables();

  // 비교값 해석 (숫자 | ':변수명' | 숫자 문자열)
  const resolvedComparisonValue = useMemo(() => {
    if (comparisonValue === undefined || comparisonValue === null) return undefined;
    if (typeof comparisonValue === 'number') return comparisonValue;
    if (typeof comparisonValue === 'string') {
      const trimmed = comparisonValue.trim();
      // ':varName' 형태 처리
      if (trimmed.startsWith(':')) {
        const varName = trimmed.slice(1);
        const v = getVariable ? getVariable(varName, undefined) : undefined;
        const n = typeof v === 'string' ? parseFloat(v) : (typeof v === 'number' ? v : undefined);
        return Number.isFinite(n) ? n : undefined;
      }
      // 숫자 문자열 처리
      const n = parseFloat(trimmed);
      return Number.isFinite(n) ? n : undefined;
    }
    return undefined;
  }, [comparisonValue, getVariable]);
  
  // 비교값의 퍼센트 계산
  const comparisonPercentage = resolvedComparisonValue !== undefined ? 
    ((resolvedComparisonValue - min) / (max - min)) * 100 : 0;
  
  // 색상 단계 설정 (기본값 또는 사용자 정의)
  // 색상 단계: 낮은 값은 붉은/주황, 높은 값은 녹색 계열
  const colorSteps = useMemo(() => {
    const steps = actualOptions?.colorSteps;
    // 사용자 정의 배열이 [fraction,color] 형태일 수 있어 정규화
    if (Array.isArray(steps) && steps.length > 0) {
      return steps.map((s) => {
        if (Array.isArray(s) && s.length === 2) {
          return { threshold: Math.round((s[0] || 0) * 100), color: s[1] };
        }
        return s;
      }).map((s) => ({ threshold: typeof s.threshold === 'number' ? s.threshold : 100, color: s.color || '#52c41a' }));
    }
    return [
      { threshold: 20, color: '#ff4d4f' },  // 0-20%: 빨간색
      { threshold: 40, color: '#fa8c16' },  // 20-40%: 진한 주황
      { threshold: 60, color: '#fadb14' },  // 40-60%: 노란색
      { threshold: 80, color: '#73d13d' },  // 60-80%: 연두색
      { threshold: 100, color: '#52c41a' }  // 80-100%: 녹색
    ];
  }, [actualOptions?.colorSteps]);
  
  // chartData가 없는 경우 처리 - 모든 Hook 호출 이후에 위치
  if (!chartData) {
    console.error('[ProgressBarRenderer] No chartData received!');
    return (
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        height: '100%',
        color: '#999',
        fontSize: '14px'
      }}>
        데이터를 불러오는 중...
      </div>
    );
  }
  
  // 현재 값에 따른 색상 결정
  const getColorForValue = (percent) => {
    for (let step of colorSteps) {
      if (percent <= step.threshold) {
        return step.color;
      }
    }
    return colorSteps[colorSteps.length - 1].color;
  };
  
  const currentColor = getColorForValue(percentage);
  
  // 스타일 옵션
  const style = actualOptions?.style || {};
  const showTitle = actualOptions?.showTitle !== false;
  
  // 값 포맷터: 천 단위 구분 기호 적용 (예: 1,234)
  const formatNumberWithThousands = (numericValue, fractionDigits = 0) => {
    const safeNumber = Number.isFinite(numericValue) ? numericValue : 0;
    const rounded = fractionDigits > 0 
      ? Number(safeNumber).toFixed(fractionDigits)
      : Math.round(safeNumber);
    // toFixed는 문자열을 반환하므로 숫자 변환 후 포맷 또는 그대로 포맷
    const numberToFormat = typeof rounded === 'string' ? Number(rounded) : rounded;
    return Number.isFinite(numberToFormat)
      ? numberToFormat.toLocaleString('en-US')
      : '0';
  };
  // 바 높이 계산 - barHeightPercentage가 있으면 기본값(18px)에 대한 퍼센트로 계산
  const defaultBarHeight = 18;
  const barHeightPercentage = actualOptions?.advanced?.barHeightPercentage;
  const barHeight = barHeightPercentage 
    ? Math.round((defaultBarHeight * barHeightPercentage) / 100)
    : (actualOptions?.advanced?.barHeight || defaultBarHeight);
  const borderRadius = actualOptions?.advanced?.borderRadius || 15;
  const showValue = actualOptions?.advanced?.showValue !== false;
  const showLabel = actualOptions?.advanced?.showLabel !== false;
  const animated = actualOptions?.advanced?.animated !== false;
  
  if (error) {
    return (
      <div className="progress-bar-error">
        <span>차트 오류: {error}</span>
      </div>
    );
  }
  
  return (
    <div className="progress-bar-container" style={{
      padding: style.padding || '8px',
      backgroundColor: style.background?.color || 'transparent',
      borderRadius: style.background?.borderRadius ? `${style.background.borderRadius}px` : '0'
    }}>
      {showTitle && title && (
        <div className="progress-bar-title" style={{
          fontSize: style.fonts?.title?.fontSize || 20,
          fontWeight: style.fonts?.title?.fontWeight || '600',
          color: style.fonts?.title?.color || '#2c3e50',
          marginBottom: '6px',
          textAlign: 'left'
        }}>
          {title}
        </div>
      )}
      
      <div className="progress-bar-wrapper" style={{
        marginTop: showComparison && comparisonLabel ? '30px' : '0',
        marginBottom: showComparison && !comparisonLabel ? '25px' : '0'
      }}>
        {showLabel && name && (
          <div className="progress-bar-label" style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            marginBottom: '4px'
          }}>
            <span style={{ fontSize: '14px', color: '#444', fontWeight: 500 }}>{name}</span>
          </div>
        )}
        
        <div 
          className="progress-bar-track" 
          style={{
            height: `${barHeight}px`,
            backgroundColor: '#f0f0f0',
            borderRadius: `${borderRadius}px`,
            position: 'relative',
            overflow: 'visible',
            boxShadow: 'inset 0 2px 4px rgba(0,0,0,0.1)'
          }}
        >
          <div 
            className={`progress-bar-fill ${animated ? 'animated' : ''}`}
            style={{
              width: `${Math.min(100, Math.max(0, percentage))}%`,
              height: '100%',
              backgroundColor: currentColor,
              borderRadius: `${borderRadius}px`,
              transition: animated ? 'width 0.5s ease-in-out, background-color 0.3s ease' : 'none',
              boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
              background: `linear-gradient(180deg, ${currentColor} 0%, ${currentColor}dd 100%)`,
              position: 'relative',
              zIndex: 5
            }}
          />
          
          {/* 중앙 값 오버레이 */}
          {showValue && (
            <div className="progress-bar-value" style={{
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              fontSize: `${parseInt(style.fonts?.value?.fontSize || 13, 10)}px`,
              fontWeight: style.fonts?.value?.fontWeight || 'bold',
              fontStyle: style.fonts?.value?.fontStyle || 'normal',
              color: style.fonts?.value?.color || (percentage > 50 ? '#fff' : '#333'),
              textShadow: style.fonts?.value?.color ? 'none' : (percentage > 50 ? '0 1px 2px rgba(0,0,0,0.3)' : 'none'),
              zIndex: 10,
              whiteSpace: 'nowrap'
            }}>
              {formatNumberWithThousands(value, 0)}{unit}
            </div>
          )}
          
          {/* 비교값 표시 */}
          {showComparison && resolvedComparisonValue !== undefined && (
            <>
              {/* 비교선 */}
              <div style={{
                position: 'absolute',
                left: `${Math.min(100, Math.max(0, comparisonPercentage))}%`,
                top: 0,
                height: '100%',
                width: `${comparisonLineWidth}px`,
                backgroundColor: comparisonColor,
                borderLeft: comparisonLineStyle === 'dashed' ? `${comparisonLineWidth}px dashed ${comparisonColor}` : 
                           comparisonLineStyle === 'dotted' ? `${comparisonLineWidth}px dotted ${comparisonColor}` : 
                           'none',
                borderRight: comparisonLineStyle !== 'solid' ? 'none' : undefined,
                zIndex: 15,
                boxShadow: '0 0 4px rgba(0,0,0,0.2)'
              }} />
              
              {/* 비교값 라벨 (선 위에 표시) */}
              {comparisonLabel && (
                <div style={{
                  position: 'absolute',
                  left: `${Math.min(100, Math.max(0, comparisonPercentage))}%`,
                  top: '-25px',
                  transform: 'translateX(-50%)',
                  fontSize: '11px',
                  fontWeight: '500',
                  color: comparisonColor,
                  whiteSpace: 'nowrap',
                  zIndex: 16,
                  background: 'white',
                  padding: '2px 6px',
                  borderRadius: '3px',
                  border: `1px solid ${comparisonColor}`,
                  boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
                }}>
                  {comparisonLabel}: {formatNumberWithThousands(resolvedComparisonValue, 0)}{unit}
                </div>
              )}
              
              {/* 비교값만 표시 (라벨이 없을 때) */}
              {!comparisonLabel && (
                <div style={{
                  position: 'absolute',
                  left: `${Math.min(100, Math.max(0, comparisonPercentage))}%`,
                  bottom: '-20px',
                  transform: 'translateX(-50%)',
                  fontSize: '10px',
                  fontWeight: '600',
                  color: comparisonColor,
                  whiteSpace: 'nowrap',
                  zIndex: 16
                }}>
                  {formatNumberWithThousands(resolvedComparisonValue, 0)}{unit}
                </div>
              )}
            </>
          )}
        </div>
        
        {/* 눈금 표시 (선택적) */}
        {actualOptions?.advanced?.showScale && (
          <div className="progress-bar-scale" style={{
            display: 'flex',
            justifyContent: 'space-between',
            marginTop: '5px',
            fontSize: '11px',
            color: '#999'
          }}>
            <span>{min}</span>
            <span>{((max - min) / 2 + min).toFixed(0)}</span>
            <span>{max}</span>
          </div>
        )}
      </div>
      
      {/* 추가 정보 표시 */}
      {actualOptions?.advanced?.showDetails && (
        <div className="progress-bar-details" style={{
          marginTop: '10px',
          fontSize: '12px',
          color: '#666',
          textAlign: 'center'
        }}>
          <span>상태: </span>
          <span style={{ color: currentColor, fontWeight: 'bold' }}>
            {percentage <= 20 ? '위험' :
             percentage <= 40 ? '주의' :
             percentage <= 60 ? '보통' :
             percentage <= 80 ? '양호' : '우수'}
          </span>
        </div>
      )}
    </div>
  );
};

export default ProgressBarRenderer;